import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { CartProvider } from './shared/context/CartContext';
import { WishlistProvider } from './shared/context/WishlistContext';
import { QuickViewProvider } from './shared/context/QuickViewContext';
import { ToastProvider } from './shared/context/ToastContext';
import { ConfirmProvider } from './shared/context/ConfirmContext';
import { CartDrawerProvider } from './shared/context/CartDrawerContext';
import { HomepageCacheProvider } from './shared/context/HomepageCacheContext';
import { ProductsCacheProvider } from './shared/context/ProductsCacheContext';
import { ToastContainer } from './shared/components/ToastContainer';
import { useToastContext } from './shared/context/ToastContext';
import ErrorBoundary from './shared/components/ErrorBoundary';
import ScrollToTop from './shared/components/ScrollToTop';
import { newTheme } from './storefront/theme/newTheme';
import { GlobalStyles } from './storefront/styles/GlobalStyles';
import './App.css';

// Shared layout (loaded immediately)
import NewLayout from './storefront/components/NewLayout';
import { AdminLayout } from './admin/components/layout/AdminLayout';

// Lazy load storefront pages for code splitting
const NewHomePage = lazy(() => import('./storefront/pages/NewHomePage'));
const NewProductDetailPage = lazy(() => import('./storefront/pages/NewProductDetailPage'));
const NewCheckoutPage = lazy(() => import('./storefront/pages/NewCheckoutPage'));
const ProductListingPage = lazy(() => import('./storefront/pages/ProductListingPage'));
const NewWishlistPage = lazy(() => import('./storefront/pages/NewWishlistPage'));
const OrderLookupPage = lazy(() => import('./storefront/pages/OrderLookupPage'));
const ReturnsPage = lazy(() => import('./storefront/pages/ReturnsPage'));
const CategoriesPage = lazy(() => import('./storefront/pages/CategoriesPage'));
const CategoriesListingPage = lazy(() => import('./storefront/pages/CategoriesListingPage'));
const CollectionsListingPage = lazy(() => import('./storefront/pages/CollectionsListingPage'));
const ContactPage = lazy(() => import('./storefront/pages/ContactPage'));
const OurStoryPage = lazy(() => import('./storefront/pages/OurStoryPage'));
const CookiePolicyPage = lazy(() => import('./storefront/pages/CookiePolicyPage'));
const CareersPage = lazy(() => import('./storefront/pages/CareersPage'));
const ShippingPage = lazy(() => import('./storefront/pages/ShippingPage'));
const PrivacyPolicyPage = lazy(() => import('./storefront/pages/PrivacyPolicyPage'));
const ReturnsPolicyPage = lazy(() => import('./storefront/pages/ReturnsPolicyPage'));
const BlogDetailPage = lazy(() => import('./storefront/pages/BlogDetailPage'));

// Lazy load admin pages
const AdminLoginPage = lazy(() => import('./admin/pages/AdminLoginPage'));
const DashboardPage = lazy(() => import('./admin/pages/DashboardPage'));
const ProductsListPage = lazy(() => import('./admin/pages/products/ProductsListPage'));
const ProductFormPage = lazy(() => import('./admin/pages/products/ProductFormPage'));
const OrdersListPage = lazy(() => import('./admin/pages/orders/OrdersListPage'));
const OrderDetailPage = lazy(() => import('./admin/pages/orders/OrderDetailPage'));
const CreateOrderPage = lazy(() => import('./admin/pages/orders/CreateOrderPage'));
const EditOrderPage = lazy(() => import('./admin/pages/orders/EditOrderPage'));
const CustomersListPage = lazy(() => import('./admin/pages/customers/CustomersListPage'));
const CustomerDetailPage = lazy(() => import('./admin/pages/customers/CustomerDetailPage'));
const CreateCustomerPage = lazy(() => import('./admin/pages/customers/CreateCustomerPage'));
const AnalyticsPage = lazy(() => import('./admin/pages/AnalyticsPage'));
const MarketingPage = lazy(() => import('./admin/pages/MarketingPage'));
const CreateCampaignPage = lazy(() => import('./admin/pages/marketing/CreateCampaignPage'));
const EditCampaignPage = lazy(() => import('./admin/pages/marketing/EditCampaignPage'));
const CampaignDetailsPage = lazy(() => import('./admin/pages/marketing/CampaignDetailsPage'));
const SalesChannelsPage = lazy(() => import('./admin/pages/sales-channels/SalesChannelsPage'));
const SettingsPage = lazy(() => import('./admin/pages/SettingsPage'));
const AdminCategoriesPage = lazy(() => import('./admin/pages/AdminCategoriesPage'));
const AdminCollectionsPage = lazy(() => import('./admin/pages/AdminCollectionsPage'));
const AdminDiscountsPage = lazy(() => import('./admin/pages/AdminDiscountsPage'));
const AdminVouchersPage = lazy(() => import('./admin/pages/AdminVouchersPage'));
const AdminShippingRatesPage = lazy(() => import('./admin/pages/AdminShippingRatesPage'));
const AdminReviewsPage = lazy(() => import('./admin/pages/AdminReviewsPage'));
const AdminInventoryPage = lazy(() => import('./admin/pages/AdminInventoryPage'));
const AdminMentorPage = lazy(() => import('./admin/pages/AdminMentorPage'));


// Online Store pages
const OnlineStoreOverviewPage = lazy(() => import('./admin/pages/online-store/OnlineStoreOverviewPage'));
const BlogPostsListPage = lazy(() => import('./admin/pages/online-store/BlogPostsListPage'));
const BlogPostFormPage = lazy(() => import('./admin/pages/online-store/BlogPostFormPage'));


// Loading component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '100vh',
    fontFamily: 'Inter, sans-serif'
  }}>
    Loading...
  </div>
);

// Redirect component for old collection URLs
const CollectionRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/products?collection=${id}`} replace />;
};

const AppContent = () => {
  const { toasts, dismissToast } = useToastContext();
  
  return (
    <>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />
        <ProductsCacheProvider>
          <HomepageCacheProvider>
            <CartProvider>
              <CartDrawerProvider>
                <WishlistProvider>
                  <QuickViewProvider>
                <Suspense fallback={<LoadingFallback />}>
              <Routes>
                  {/* Storefront routes */}
                  <Route path="/" element={<NewLayout />}>
                    <Route index element={<NewHomePage />} />
                    <Route path="products" element={<ProductListingPage />} />
                    <Route path="products/:handle" element={<NewProductDetailPage />} />
                    <Route path="categories/:slug" element={<CategoriesPage key="category-slug" />} />
                    <Route path="categories" element={<CategoriesListingPage />} />
                    <Route path="collections" element={<CollectionsListingPage />} />
                    <Route path="collections/:id" element={<CollectionRedirect />} />
                    <Route path="checkout" element={<NewCheckoutPage />} />
                    <Route path="wishlist" element={<NewWishlistPage />} />
                    <Route path="search" element={<NewWishlistPage />} />
                    <Route path="order-lookup" element={<OrderLookupPage />} />
                    <Route path="orders" element={<OrderLookupPage />} />
                    <Route path="returns" element={<ReturnsPage />} />
                    <Route path="contact" element={<ContactPage />} />
                    <Route path="our-story" element={<OurStoryPage />} />
                    <Route path="cookie-policy" element={<CookiePolicyPage />} />
                    <Route path="careers" element={<CareersPage />} />
                    <Route path="shipping" element={<ShippingPage />} />
                    <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="returns-policy" element={<ReturnsPolicyPage />} />
                    <Route path="blogs/:handle" element={<BlogDetailPage />} />
                  </Route>

                  {/* Admin routes */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<DashboardPage />} />
                    <Route path="login" element={<AdminLoginPage />} />
                    <Route path="products" element={<ProductsListPage />} />
                    <Route path="products/new" element={<ProductFormPage />} />
                    <Route path="products/:id" element={<ProductFormPage />} />
                    <Route path="orders" element={<OrdersListPage />} />
                    <Route path="orders/create" element={<CreateOrderPage />} />
                    <Route path="orders/:id" element={<OrderDetailPage />} />
                    <Route path="orders/:id/edit" element={<EditOrderPage />} />
                    <Route path="customers" element={<CustomersListPage />} />
                    <Route path="customers/new" element={<CreateCustomerPage />} />
                    <Route path="customers/:id" element={<CustomerDetailPage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="marketing" element={<MarketingPage />} />
                    <Route path="marketing/campaigns/create" element={<CreateCampaignPage />} />
                    <Route path="marketing/campaigns/:id/edit" element={<EditCampaignPage />} />
                    <Route path="marketing/campaigns/:id" element={<CampaignDetailsPage />} />
                    <Route path="discounts" element={<AdminDiscountsPage />} />
                    <Route path="online-store" element={<OnlineStoreOverviewPage />} />
                    <Route path="online-store/blog" element={<BlogPostsListPage />} />
                    <Route path="online-store/blog/new" element={<BlogPostFormPage />} />
                    <Route path="online-store/blog/:id" element={<BlogPostFormPage />} />
                    <Route path="sales-channels" element={<SalesChannelsPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="categories" element={<AdminCategoriesPage />} />
                    <Route path="collections" element={<AdminCollectionsPage />} />
                    <Route path="vouchers" element={<AdminVouchersPage />} />
                    <Route path="shipping-rates" element={<AdminShippingRatesPage />} />
                    <Route path="reviews" element={<AdminReviewsPage />} />
                    <Route path="inventory" element={<AdminInventoryPage />} />
                    <Route path="mentor" element={<AdminMentorPage />} />

                  </Route>

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
                  </QuickViewProvider>
                </WishlistProvider>
              </CartDrawerProvider>
            </CartProvider>
          </HomepageCacheProvider>
        </ProductsCacheProvider>
      </Router>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
        <ThemeProvider theme={newTheme}>
        <GlobalStyles />
        <ToastProvider>
          <ConfirmProvider>
            <AppContent />
          </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
